import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { buildCurrencyInputRules } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Drawer, Form, Input, Space } from 'antd';
import { useState } from 'react';
export const Route = createFileRoute('/admin-console/platform-content/products/$serviceId')({
    staticData: {
        ui: {
            title: 'Edit service',
            subtitle: 'Edit service details.',
        },
        presentation: 'overlay',
    },
    params: {
        parse(params) {
            return {
                serviceId: Number(params.serviceId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EditService />
        </PageController>
    );
}

function EditService() {
    const { serviceId } = Route.useParams();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const { data: service } = trpc.admin.service.getService.useQuery({ id: serviceId });

    const updateServiceMutation = trpc.admin.service.updateService.useMutation();

    async function handleSave() {
        try {
            setSaving(true);
            message.loading('Saving...');
            const values = await form.validateFields();
            await updateServiceMutation.mutateAsync({
                id: serviceId,
                set: {
                    name: values.name,
                    description: values.description,
                    unitPrice: values.unitPrice,
                },
            });
            await trpcUtils.admin.service.invalidate();
            message.success('Service updated successfully');
            setOpen(false);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Drawer
            title="Edit service"
            open={open}
            onClose={() => setOpen(false)}
            afterOpenChange={(open) => {
                if (!open) {
                    navigate({
                        to: '/admin-console/platform-content/products',
                    });
                }
            }}
            size={500}
            footer={
                <Space className="flex justify-end">
                    <Button type="secondary" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>
                        Save Changes
                    </Button>
                </Space>
            }
        >
            {service && (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        name: service.name,
                        code: service.code,
                        description: service.description || undefined,
                        unitPrice: service.unitPrice,
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter the service name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item name="code" label="Code">
                        <Input readOnly className="bg-muted" />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                        <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />
                    </Form.Item>

                    <Form.Item
                        name="unitPrice"
                        label="Unit Price"
                        rules={buildCurrencyInputRules({ isRequired: true })}
                    >
                        <CurrencyInput />
                    </Form.Item>
                </Form>
            )}
        </Drawer>
    );
}
